import React from 'react';
import { saveTransferShipment, type TransferRecord, type TransferRecordShipment } from '@/data/transferRecord';
import TransferRecordDialog from './TransferRecordDialog';
import TransferShipmentFields, {
  EMPTY_SHIPMENT,
  shipmentDraftFrom,
  shipmentDraftIsEmpty,
  shipmentInputFrom,
  type ShipmentDraft,
} from './TransferShipmentFields';
import { shipmentTitle, toSiteInputValue } from './transferRecordView';
import type { TransferActionRunner } from './useTransferRecord';

type TransferShipmentDialogProps = {
  record: TransferRecord;
  /** The shipment to edit; null adds one. */
  shipment: TransferRecordShipment | null;
  runner: TransferActionRunner;
  onClose: () => void;
  onDone: (message: string) => void;
};

/** Add or edit a shipment (a carrier parcel or an ITA truck run). Works on R2 transfers too. */
export default function TransferShipmentDialog({ record, shipment, runner, onClose, onDone }: TransferShipmentDialogProps) {
  const { transfer } = record;
  const fromZone = transfer.fromSite?.timezone ?? null;
  const toZone = transfer.toSite?.timezone ?? null;
  const [draft, setDraft] = React.useState<ShipmentDraft>(() => (shipment
    ? shipmentDraftFrom(shipment, fromZone, toZone)
    : { ...EMPTY_SHIPMENT, shippedAt: toSiteInputValue(transfer.actualShipAt, fromZone) }));
  const [confirmRemove, setConfirmRemove] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleChange = React.useCallback((next: ShipmentDraft) => {
    setDraft(next);
    setError(null);
  }, []);

  async function save() {
    if (shipmentDraftIsEmpty(draft)) {
      setError('Add a carrier, a vehicle, a driver or a tracking number.');
      return;
    }
    const input = { ...shipmentInputFrom(draft, { withTimes: true, fromZone, toZone }), id: shipment?.id ?? null };
    setSaving(true);
    setError(null);
    try {
      await runner.run(
        shipment ? `shipment:${shipment.id}` : 'shipment-add',
        input,
        (requestKey) => saveTransferShipment(transfer.id, requestKey, input),
        { fallback: 'The shipment was not saved.' },
      );
      onDone(shipment ? 'Shipment saved' : 'Shipment added');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The shipment was not saved.');
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!shipment) return;
    if (!confirmRemove) {
      setConfirmRemove(true);
      return;
    }
    const input = { id: shipment.id, delete: true };
    setSaving(true);
    setError(null);
    try {
      await runner.run(
        `shipment-remove:${shipment.id}`,
        input,
        (requestKey) => saveTransferShipment(transfer.id, requestKey, input),
        { fallback: 'The shipment was not removed.' },
      );
      onDone('Shipment removed');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The shipment was not removed.');
      setConfirmRemove(false);
    } finally {
      setSaving(false);
    }
  }

  function handleRemoveClick() {
    void remove();
  }

  return (
    <TransferRecordDialog
      title={shipment ? `Edit ${shipmentTitle(shipment)}` : 'Add a shipment'}
      eyebrow={transfer.docNo ?? 'Transfer'}
      subtitle="Shipments record how the units travel: a carrier parcel or an ITA truck run."
      onClose={onClose}
      busy={saving}
      error={error}
      onSubmit={() => { void save(); }}
      size="lg"
      footer={(
        <>
          {shipment ? (
            <button type="button" className="btn danger transfer-record-dialog__remove" onClick={handleRemoveClick} disabled={saving}>
              {confirmRemove ? 'Remove this shipment?' : 'Remove shipment'}
            </button>
          ) : null}
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : shipment ? 'Save shipment' : 'Add shipment'}
          </button>
        </>
      )}
    >
      <TransferShipmentFields
        value={draft}
        onChange={handleChange}
        fromSiteId={transfer.fromSite?.id ?? null}
        fromZone={fromZone}
        toZone={toZone}
        withTimes
        disabled={saving}
      />
    </TransferRecordDialog>
  );
}
